import {
  getUsersSummary,
  getAllCounterparties,
  getLegalEntityCounterparty,
  getPhysicalEntityCounterparty,
  getCounterpartyBankAccounts,
} from "../../../../api/api";
import { getAgreements } from "../../../../api/agreementsApi";
import { formatDateForApi, matchesPerformerType } from "../Agreements/agreementFormHelpers";

export const PERFORMER_TYPES = [
  { value: "staff_employee", label: "Штатный сотрудник", source: "user" },
  { value: "individual_gph", label: "Физ лицо на ГПХ", source: "counterparty" },
  { value: "too_without_vat", label: "ТОО без НДС", source: "counterparty" },
  { value: "ip", label: "ИП", source: "counterparty" },
  { value: "too_with_vat", label: "ТОО с НДС", source: "counterparty" },
];

export const getCounterpartyLabel = (c) =>
  c?.full_name ||
  c?.full_company_name ||
  c?.short_company_name ||
  c?.short_name ||
  (c?.id != null ? `Контрагент #${c.id}` : "");

export const getCounterpartyBin = (c) =>
  String(c?.bin_or_inn || c?.bin_iin || "").replace(/\D/g, "");

export const getCounterpartySelectLabel = (c) => {
  const name = getCounterpartyLabel(c);
  const bin = getCounterpartyBin(c);
  return bin ? `${name} / ${bin}` : name;
};

export const filterCounterpartySelectOption = (option, rawInput) => {
  const query = String(rawInput || "").trim().toLowerCase();
  if (!query) return true;
  const label = String(option?.label || "").toLowerCase();
  if (label.includes(query)) return true;
  const data = option?.data?.data || option?.data;
  const bin = getCounterpartyBin(data);
  const queryDigits = query.replace(/\D/g, "");
  return Boolean(queryDigits && bin.includes(queryDigits));
};

export const getUserLabel = (user) => {
  const name =
    user?.full_name?.trim() ||
    [
      user?.surname || user?.lastName,
      user?.name || user?.firstName,
      user?.patronymic || user?.middleName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
  return name || `Сотрудник #${user?.id}`;
};

export const mapPerformerTypeToApi = (type) => {
  if (type === "staff_employee") return "staff_employee";
  if (type === "individual_gph") return "gph_physical";
  if (type === "ip" || type === "too_without_vat" || type === "too_with_vat") {
    return "counterparty";
  }
  return type;
};

export const filterCounterpartiesByType = (items, performerType) => {
  const arr = Array.isArray(items) ? items : [];
  return arr.filter((item) => matchesPerformerType(item, performerType));
};

export const loadPerformerOptions = async (performerType) => {
  const typeMeta = PERFORMER_TYPES.find((t) => t.value === performerType);
  if (!typeMeta) return [];

  if (typeMeta.source === "user") {
    const usersData = await getUsersSummary({ isActive: true, pageSize: 500 });
    const users = usersData?.users || [];
    return users.map((user) => ({
      value: user.id,
      label: getUserLabel(user),
      data: { _source: "user", ...user },
    }));
  }

  const list = await getAllCounterparties({ counterparty_side: "counterparty" });
  return filterCounterpartiesByType(list, performerType).map((item) => ({
    value: item.id,
    label: getCounterpartySelectLabel(item),
    data: { _source: "counterparty", type: item.type, ...item },
  }));
};

export const formatCounterpartyAddress = (c) => {
  if (!c) return "—";
  const parts = [c.country, c.postal_code, c.city, c.street, c.building].filter(Boolean);
  if (parts.length) return parts.join(", ");
  if (c.legal_address) return c.legal_address;
  return "—";
};

export const normalizeCounterpartyDetails = (data) => {
  if (!data) return null;
  return {
    ...data,
    bin_or_inn: data.bin_or_inn ?? data.bin_iin,
    legal_address: formatCounterpartyAddress(data),
  };
};

export const loadPerformerDetails = async (performer) => {
  if (!performer) return null;

  if (performer._source === "user") {
    return {
      type: "user",
      fullName: getUserLabel(performer),
      email: performer.email || "—",
      phone: performer.phone || "—",
    };
  }

  const entityKind =
    performer.type === "physical_entity" ? "physical_entity" : "legal_entity";
  const fetchData =
    entityKind === "physical_entity"
      ? getPhysicalEntityCounterparty(performer.id)
      : getLegalEntityCounterparty(performer.id);

  const [details, bankAccountsRaw] = await Promise.all([
    fetchData,
    getCounterpartyBankAccounts(performer.id).catch(() => []),
  ]);

  const normalized = normalizeCounterpartyDetails(details);
  const bankAccounts = Array.isArray(bankAccountsRaw) ? bankAccountsRaw : [];
  const primaryAccount =
    bankAccounts.find((acc) => acc.is_primary) || bankAccounts[0] || null;

  const contactPerson =
    normalized.type === "legal_entity"
      ? normalized.full_name_first_supervisor || normalized.full_name || "—"
      : normalized.full_name || "—";

  return {
    type: "counterparty",
    bin: normalized.bin_or_inn || "—",
    legalAddress: normalized.legal_address || "—",
    bankName: primaryAccount?.bank_name || "—",
    bik: primaryAccount?.bik || "—",
    iik: primaryAccount?.iik || "—",
    contactPerson,
    phone: normalized.phone || "—",
    email: normalized.email || "—",
  };
};

export const parseAgreementNumber = (value) => {
  if (value == null) return 0;
  const parsed = parseInt(String(value).replace(/\D/g, ""), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getNextAgreementNumber = async () => {
  const data = await getAgreements({
    ordering: "-number",
    page: 1,
    page_size: 1,
  });
  const items = data?.results || [];
  const maxNumber = items.reduce((max, item) => {
    const current = parseAgreementNumber(item?.number);
    return current > max ? current : max;
  }, 0);
  return maxNumber + 1;
};

export const buildAddAgreementPayload = ({
  projectId,
  performerType,
  selectedPerformer,
  agreementNumber,
}) => {
  const payload = {
    number: agreementNumber,
    status: "draft",
    date_from: formatDateForApi(),
    date_to: formatDateForApi(),
    performer_type: mapPerformerTypeToApi(performerType),
    project_id: Number(projectId) || 0,
    price: 0,
    smeta_version: 0,
    block_ids: [],
    section_ids: [],
  };

  if (selectedPerformer?._source === "counterparty") {
    payload.counterparty_id = Number(selectedPerformer.id) || 0;
  }
  if (selectedPerformer?._source === "user") {
    payload.performer_ids = [Number(selectedPerformer.id) || 0];
  }

  return payload;
};
